/* O VEREDITO
 * Sete estados mutuamente exclusivos, direto de `recomendar(p)`.
 *
 * REGRA DE OURO: no estado ACIMA nada sugere baixar preço. O tipo
 * `RecomendacaoAcima` nem tem os campos de reajuste, então nem por engano. */

import { percentuaisVariaveis } from '@favo/core';
import { Link } from 'react-router-dom';

import { CelulaNivel } from '@/componentes/celula-nivel';
import { PainelVeredito } from '@/componentes/painel-veredito';
import { Button } from '@/componentes/ui/button';
import { Valor } from '@/componentes/valor';
import type { Parametros, Recomendacao } from '@/dados';
import { formatarMoeda, formatarPercentual, formatarPercentualPontos } from '@/design/formato';

import { sinalMoeda } from './grandezas';
import type { NavegarPara } from './salvamento';

export function Veredito({
  rec,
  p,
  semEmpresa,
  semProjeto,
  aoAdotar,
  aoNavegar,
}: {
  rec: Recomendacao;
  p: Parametros;
  semEmpresa: boolean;
  semProjeto: boolean;
  aoAdotar: () => void;
  aoNavegar: NavegarPara;
}) {
  const meta = formatarPercentualPontos(p.lucroAlvo);

  if (rec.estado === 'sem-dados') {
    const falta = semEmpresa
      ? {
          texto: 'Cadastre a empresa em Empresa para o motor saber com que custos calcular.',
          rota: '/empresa',
          elo: 'Ir para Empresa',
        }
      : semProjeto
        ? {
            texto:
              'Nenhum projeto em análise. Crie o projeto para informar potência, módulos e preço.',
            rota: '/projeto?novo=1',
            elo: 'Criar projeto',
          }
        : rec.falta === 'ticket'
          ? {
              texto:
                'Falta o preço de venda. Informe no campo Preço de venda aqui embaixo, ou em Dados do projeto.',
              rota: '/projeto',
              elo: 'Ir para Dados do projeto',
            }
          : {
              texto:
                'Falta o volume: quantos projetos você vende por mês. Sem ele não há rateio da despesa fixa nem preço mínimo.',
              rota: '/indicadores',
              elo: 'Ir para Indicadores',
            };

    return (
      <PainelVeredito
        assenta
        atraso={60}
        selo={{ tom: 'neutro', texto: 'Sem dados para o veredito' }}
        rotulo="O que falta para calcular"
        explicacao={<p>{falta.texto}</p>}
        acoes={
          <Button asChild>
            <Link to={falta.rota} onClick={aoNavegar(falta.rota)}>
              {falta.elo}
            </Link>
          </Button>
        }
      />
    );
  }

  if (rec.estado === 'percentual-invalido') {
    // `valor` vem em PONTOS percentuais, então o formatador é o de pontos.
    // Trocar pelo de fração daria erro de 100× com cara de plausível.
    const lista = rec.invalidos
      .map((i) => `${i.campo} (${formatarPercentualPontos(i.valor)})`)
      .join(', ');
    // Roteia pela CHAVE, nunca pelo rótulo: comparar com o texto de exibição
    // amarraria a navegação à redação, e trocar um rótulo quebraria o
    // roteamento em silêncio, com tipo intacto e teste verde.
    //
    // A meta é editada nesta tela, nas entradas; todo o resto mora em
    // Despesas. O caso MISTO precisa dizer os dois lugares, senão manda o
    // usuário resolver metade e voltar sem saber por quê.
    const temMeta = rec.invalidos.some((i) => i.chave === 'lucroAlvo');
    const temDespesa = rec.invalidos.some((i) => i.chave !== 'lucroAlvo');
    const onde = temMeta
      ? temDespesa
        ? 'Corrija os percentuais em Despesas, e a meta em Custos e metas da empresa, aqui embaixo.'
        : 'A meta de lucro fica em Custos e metas da empresa, aqui embaixo.'
      : 'Corrija os percentuais em Despesas.';

    return (
      <PainelVeredito
        assenta
        atraso={60}
        selo={{ tom: 'negativo', texto: 'Percentual fora da faixa' }}
        rotulo="Corrija antes de precificar"
        explicacao={
          <p>
            {lista} {rec.invalidos.length > 1 ? 'estão' : 'está'} fora da faixa de 0% a 100%.
            Enquanto houver percentual fora da faixa o preço mínimo não é calculado. {onde}
          </p>
        }
        acoes={
          temDespesa ? (
            <Button asChild>
              <Link to="/despesas" onClick={aoNavegar('/despesas')}>
                Rever despesas
              </Link>
            </Button>
          ) : undefined
        }
      />
    );
  }

  if (rec.estado === 'meta-inviavel') {
    const pcts = percentuaisVariaveis(p);
    return (
      <PainelVeredito
        assenta
        atraso={60}
        selo={{ tom: 'negativo', texto: 'Meta inviável' }}
        rotulo="Nenhum preço fecha esta conta"
        cifra={<Valor valor={null} formato="moeda" tamanho="display" tom="apagado" />}
        explicacao={
          <p>
            Os percentuais sobre a venda somam {formatarPercentual(pcts.total + pcts.serv)} e a meta
            pede mais {meta}. Junto, passa de 100% do preço: cada real a mais de preço traz junto
            mais comissão, mais tributo e mais meta, então aumentar não resolve. Reveja os
            percentuais em Despesas ou a meta de lucro aqui na tela.
          </p>
        }
        acoes={
          <Button asChild>
            <Link to="/despesas" onClick={aoNavegar('/despesas')}>
              Rever despesas
            </Link>
          </Button>
        }
      />
    );
  }

  if (rec.estado === 'prejuizo') {
    const fracao = p.ticket / rec.precoSugerido;
    const perda = rec.atual.lucroUnit === null ? null : Math.abs(rec.atual.lucroUnit);
    return (
      <PainelVeredito
        assenta
        atraso={60}
        selo={{ tom: 'negativo', texto: 'Prejuízo no preço de hoje' }}
        rotulo={`Preço mínimo para a meta de ${meta}`}
        cifra={<Valor valor={rec.precoSugerido} formato="moeda" tamanho="display" />}
        de={formatarMoeda(p.ticket)}
        para={formatarMoeda(rec.precoSugerido)}
        variacao={`${sinalMoeda(rec.reajuste)} · ${formatarPercentual(rec.reajustePct)}`}
        explicacao={
          <>
            <p>
              Cada projeto vendido a {formatarMoeda(p.ticket)}
              {perda === null ? ' não se paga' : ` perde ${formatarMoeda(perda)}`}, depois de
              comissão, tributo e do rateio da despesa fixa. Este não é um preço abaixo da meta, é
              um preço abaixo do custo. O projeto só passa a se pagar a partir de{' '}
              {formatarMoeda(rec.precoLucroZero)}, e só entrega a meta de {meta} a partir de{' '}
              {formatarMoeda(rec.precoSugerido)}.
            </p>
            {rec.reducaoKit === null ? null : (
              <p className="border-claro-fio mt-3 border-t pt-3">
                Outro caminho, mesmo resultado: mantendo o preço em {formatarMoeda(p.ticket)}, o KIT
                precisa sair {formatarMoeda(rec.reducaoKit)} mais barato do que os{' '}
                {formatarMoeda(p.kit)} de hoje. Serve de régua na negociação com a distribuidora.
              </p>
            )}
          </>
        }
        medidor={
          <CelulaNivel
            fracao={fracao}
            topo={{
              rotulo: 'Preço mínimo para a meta',
              valor: formatarMoeda(rec.precoSugerido),
            }}
            falta={{ rotulo: 'O que falta', valor: sinalMoeda(rec.reajuste) }}
            nivel={{ rotulo: 'Preço de hoje', valor: formatarMoeda(p.ticket) }}
            legenda={`O projeto se paga a partir de ${formatarMoeda(rec.precoLucroZero)}.`}
            descricao={`O preço de hoje, ${formatarMoeda(p.ticket)}, está abaixo de ${formatarMoeda(rec.precoLucroZero)}, que é onde o projeto passa a se pagar. Faltam ${formatarMoeda(rec.reajuste)} para o preço mínimo da meta, ${formatarMoeda(rec.precoSugerido)}.`}
          />
        }
        acoes={<Button onClick={aoAdotar}>Adotar preço sugerido</Button>}
      />
    );
  }

  if (rec.estado === 'abaixo') {
    const fracao = p.ticket / rec.precoSugerido;
    return (
      <PainelVeredito
        assenta
        atraso={60}
        selo={{ tom: 'atencao', texto: 'Abaixo da meta' }}
        rotulo={`Preço mínimo para a meta de ${meta}`}
        cifra={<Valor valor={rec.precoSugerido} formato="moeda" tamanho="display" />}
        de={formatarMoeda(p.ticket)}
        para={formatarMoeda(rec.precoSugerido)}
        variacao={`${sinalMoeda(rec.reajuste)} · ${formatarPercentual(rec.reajustePct)}`}
        explicacao={
          <>
            <p>
              No preço de hoje sobram {formatarMoeda(rec.atual.mcUnit)} de margem de contribuição
              por projeto, e a estrutura consome {formatarMoeda(rec.sugerido.fixUnit)} de cada um. A
              partir de {formatarMoeda(rec.precoSugerido)} o projeto paga tudo e ainda entrega{' '}
              {meta} de lucro líquido.
            </p>
            {rec.reducaoKit === null ? null : (
              <p className="border-claro-fio mt-3 border-t pt-3">
                Outro caminho, mesmo resultado: mantendo o preço em {formatarMoeda(p.ticket)}, o KIT
                precisa sair {formatarMoeda(rec.reducaoKit)} mais barato do que os{' '}
                {formatarMoeda(p.kit)} de hoje. Serve de régua na negociação com a distribuidora.
              </p>
            )}
          </>
        }
        medidor={
          <CelulaNivel
            fracao={fracao}
            topo={{
              rotulo: 'Preço mínimo para a meta',
              valor: formatarMoeda(rec.precoSugerido),
            }}
            falta={{ rotulo: 'O que falta', valor: sinalMoeda(rec.reajuste) }}
            nivel={{ rotulo: 'Preço de hoje', valor: formatarMoeda(p.ticket) }}
            descricao={`O preço de hoje enche ${formatarPercentual(fracao)} da célula. Faltam ${formatarMoeda(rec.reajuste)} para alcançar o preço mínimo de ${formatarMoeda(rec.precoSugerido)}.`}
          />
        }
        acoes={<Button onClick={aoAdotar}>Adotar preço sugerido</Button>}
      />
    );
  }

  if (rec.estado === 'no-ponto') {
    return (
      <PainelVeredito
        assenta
        atraso={60}
        selo={{ tom: 'positivo', texto: 'Preço no ponto' }}
        rotulo="Preço praticado"
        cifra={<Valor valor={p.ticket} formato="moeda" tamanho="display" />}
        explicacao={
          <p>
            O preço está a menos de {formatarMoeda(rec.tolerancia)} do mínimo para a meta de {meta},
            que é {formatarMoeda(rec.precoSugerido)}. Não há reajuste a fazer. Daqui para baixo não
            sobra meta: cada real de desconto sai inteiro do lucro do ano.
          </p>
        }
        medidor={
          <CelulaNivel
            fracao={p.ticket / rec.precoSugerido}
            topo={{
              rotulo: 'Preço mínimo para a meta',
              valor: formatarMoeda(rec.precoSugerido),
            }}
            nivel={{ rotulo: 'Preço de hoje', valor: formatarMoeda(p.ticket) }}
            descricao={`O preço de hoje, ${formatarMoeda(p.ticket)}, está dentro da tolerância de ${formatarMoeda(rec.tolerancia)} do preço mínimo de ${formatarMoeda(rec.precoSugerido)}.`}
          />
        }
      />
    );
  }

  // ── acima ────────────────────────────────────────────────────────────────
  // A folga é poder de negociação, nunca convite a baixar preço.
  return (
    <PainelVeredito
      assenta
      atraso={60}
      selo={{ tom: 'positivo', texto: 'Precificação saudável' }}
      rotulo="Folga para negociar neste projeto"
      cifra={<Valor valor={rec.folga} formato="moeda" tamanho="display" tom="positivo" />}
      explicacao={
        <p>
          O preço de {formatarMoeda(p.ticket)} sustenta {formatarPercentual(rec.atual.lucroPct)} de
          lucro líquido, acima da meta de {meta}. O mínimo para a meta seria{' '}
          {formatarMoeda(rec.precoSugerido)}, então dá para ceder até {formatarMoeda(rec.folga)} (
          {formatarPercentual(rec.folgaPct)}) numa negociação antes de encostar nela. É teto de
          desconto, não preço a praticar.
        </p>
      }
      medidor={
        <CelulaNivel
          fracao={1}
          topo={{
            rotulo: 'Mínimo para a meta',
            valor: formatarMoeda(rec.precoSugerido),
          }}
          nivel={{ rotulo: 'Preço de hoje', valor: formatarMoeda(p.ticket) }}
          legenda="A célula encheu e ainda sobrou folga"
          descricao={`O preço de hoje, ${formatarMoeda(p.ticket)}, passa do mínimo de ${formatarMoeda(rec.precoSugerido)} e sobra ${formatarMoeda(rec.folga)} de folga.`}
        />
      }
    />
  );
}
